import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import './styles/global.scss'
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import "./i18n/config";
import { COOKIES_KEYS, LANGUAGES } from "./utils/constant";
import { useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "./store/store";
import { AS_MANAGER_TOKEN } from "./store/constant";
import InternetChecker from "./components/InternetChecker/InternetChecker";
import GlobalSnackbar from "./components/CustomSnackbar/CustomSnackbar";
import useClarity from './utils/useClarity';
import { useLocation } from "react-router-dom";
import TagManager from 'react-gtm-module';
import ReactPixel from 'react-facebook-pixel';
import AppRoutes from "./Routes";
import Intercom, { shutdown } from "@intercom/messenger-js-sdk";
import { getIntercomTokenAction } from "./redux/actions";
import { ThemeProvider } from "@mui/material/styles";
import { appTheme } from "./theme/theme";

function App() {
  const [cookies, setCookie,] = useCookies([
    COOKIES_KEYS?.language,
    COOKIES_KEYS?.theme,
  ]);
  const dispatch = useDispatch<AppDispatch>();

  const { i18n } = useTranslation();
  const theme = useSelector((state: RootState) => state.MRState.theme);
  const token = useSelector((state: RootState) => state.auth.token);
  const role = useSelector((state: RootState) => state.auth.role);


  const location = useLocation();
  useClarity();

  useEffect(() => {
    TagManager.initialize({ gtmId: 'GTM-NLV5CCMJ' }); // your GTM ID
    ReactPixel.init('439841685243871'); // your Pixel ID
    ReactPixel.pageView(); // track initial page load
  }, []);

  const dashboardData = useSelector((state: RootState) => state.dashboard.dashboardData);
  const intercomToken = useSelector((state: RootState) => state.dashboard.intercomToken);
  const intercomData = useSelector((state: RootState) => state.dashboard.intercomData);

  useEffect(() => {
    if (token && role === "user") {
      dispatch(getIntercomTokenAction());
    }
  }, [dispatch, token, role]);

  useEffect(() => {
    const isAdminOrManager = role === 'admin' || role === 'manager' || location.pathname.startsWith('/admin');

    if (isAdminOrManager) {
      shutdown();
      return;
    }

    const userToken = localStorage.getItem("token_user");

    if (dashboardData?.user) {
      // Wait for both the user-specific token and the secure hash
      if (!intercomToken || !intercomData || !userToken) return;
      
      const { user } = dashboardData;
      Intercom({
        app_id: "pgnvmioa",
        user_id: intercomData.userId,
        name: intercomData.name,
        email: intercomData.email,
        workspace_name: intercomData.workspaceName,
        business_names: intercomData.businessNames,
        created_at: user.createdAt ? Math.floor(new Date(user.createdAt).getTime() / 1000) : Math.floor(Date.now() / 1000),
        user_hash: intercomToken,
      });
    } else {
      shutdown();
    }
  }, [dashboardData, intercomToken, intercomData, role, location.pathname]);

  useEffect(() => {
    ReactPixel.pageView();
  }, [location]);


  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        let asManagerToken = localStorage.getItem(AS_MANAGER_TOKEN);
        if (asManagerToken != null) {
          localStorage.setItem("isManager", JSON.stringify(true));
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useLayoutEffect(() => {
    const cookieLanguage = cookies[COOKIES_KEYS?.language] ?? "";

    if (
      !cookieLanguage ||
      !LANGUAGES
        ?.map((language) => language?.code)
        ?.includes(cookieLanguage?.split("-")[0])
    ) {
      const systemLanguage = (
        Intl.DateTimeFormat().resolvedOptions().locale ?? "en-Us"
      )?.split("-")[0];
      setCookie(COOKIES_KEYS?.language, systemLanguage);
      i18n.changeLanguage(systemLanguage);
    } else {
      i18n.changeLanguage(cookieLanguage);
    }
  }, [dispatch, cookies, setCookie, i18n]);

  return (
    <ThemeProvider theme={appTheme}>
      <InternetChecker />
      <AppRoutes />
      <GlobalSnackbar />
    </ThemeProvider>
  );
}

export default App;
